import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, FileDown, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Panel } from "@/components/PageHeader";
import { api, errMsg, euro, fileUrl, fmtDateTime, pagamentoLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FiltriSoci, EMPTY_FILTERS, applyFilters } from "@/components/admin/FiltriSoci";
import { ImportSoci } from "@/components/admin/ImportSoci";
import { useSort, SortHead, byTessera } from "@/components/admin/SortTable";

const GETTERS = { tessera_numero: byTessera, importo: (m) => (m.importo == null ? null : Number(m.importo)), pagamento_confermato: (m) => (m.pagamento_confermato ? 1 : 0) };

const thisYear = new Date().getFullYear();
const YEARS = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2, thisYear - 3];

export const LibroSoci = () => {
  const [anno, setAnno] = useState(String(thisYear));
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { user } = useAuth();
  const isAdmin = user.role === "admin";

  const load = useCallback(() => api.get("/admin/memberships", { params: { anno } }).then((r) => setItems(r.data)), [anno]);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (m, v) => {
    try {
      await api.patch(`/admin/memberships/${m.id}/pagamento`, { confermato: v });
      toast.success(v ? "Pagamento confermato" : "Conferma pagamento rimossa");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resend = async (m) => {
    if (sending) return;
    setSending(m.id);
    try {
      await api.post(`/admin/memberships/${m.id}/reinvia-email`);
      toast.success(`Email con tessera in invio a ${m.cognome} ${m.nome}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(null);
    }
  };

  const exportFile = async (formato) => {
    try {
      const { data } = await api.get("/admin/memberships/export", { params: { anno, formato }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `libro_soci_${anno}.${formato}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Esportazione non riuscita");
    }
  };

  const pending = items.filter((m) => !m.pagamento_confermato).length;
  const shown = applyFilters(items, filters);
  const s = useSort(shown, GETTERS);
  const H = ({ k, label }) => <SortHead k={k} label={label} s={s} prefix="libro-soci" />;
  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h2 className="font-heading text-2xl uppercase">Libro soci</h2>
        <Select value={anno} onValueChange={setAnno}>
          <SelectTrigger className="w-28" data-testid="libro-soci-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground" data-testid="libro-soci-count">{items.length} soci{isAdmin && ` · ${pending} pagamenti da confermare`}{shown.length !== items.length && ` · ${shown.length} filtrati`}</span>
        {isAdmin && (
        <div className="flex gap-2 sm:ml-auto">
          <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="libro-soci-export-xlsx-button"><FileSpreadsheet className="h-4 w-4 mr-1 text-emerald-700" />Excel</Button>
          <Button variant="outline" onClick={() => exportFile("pdf")} data-testid="libro-soci-export-pdf-button"><FileDown className="h-4 w-4 mr-1 text-red-600" />PDF</Button>
        </div>
        )}
        {isAdmin && <p className="w-full text-xs text-muted-foreground -mt-2">L'esportazione include solo i soci (nuovi e rinnovi) con pagamento confermato.</p>}
      </div>
      {isAdmin && <ImportSoci onDone={load} />}
      <FiltriSoci f={filters} setF={setFilters} payments={isAdmin} />
      <div className="overflow-x-auto">
        <Table data-testid="libro-soci-table">
          <TableHeader>
            <TableRow>
              <H k="created_at" label="Iscrizione" /><H k="tessera_numero" label="Tessera" /><H k="cognome" label="Cognome" /><H k="nome" label="Nome" />
              <H k="categoria_socio" label="Categoria socio" /><H k="categoria_barca" label="Categoria barca" />
              {isAdmin && <><H k="importo" label="Importo" /><H k="metodo_pagamento" label="Pagamento" /><TableHead>Ricevuta</TableHead><H k="pagamento_confermato" label="Pagato" /><TableHead>Email tessera</TableHead></>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 11 : 6} className="text-center text-muted-foreground py-8">{items.length ? "Nessun socio corrisponde ai filtri" : `Nessun socio iscritto per il ${anno}`}</TableCell></TableRow>}
            {s.sorted.map((m) => (
              <TableRow key={m.id} data-testid={`libro-soci-row-${m.id}`}>
                <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(m.created_at)}</TableCell>
                <TableCell className="font-mono-x">{m.tessera_numero}</TableCell>
                <TableCell className="font-medium">{m.cognome}</TableCell>
                <TableCell>{m.nome}</TableCell>
                <TableCell>{m.categoria_socio}</TableCell>
                <TableCell>{m.categoria_barca || "—"}</TableCell>
                {isAdmin && <>
                <TableCell>{m.importo == null ? "—" : euro(m.importo)}</TableCell>
                <TableCell><Badge variant="outline">{pagamentoLabel(m.metodo_pagamento)}</Badge></TableCell>
                <TableCell>
                  {m.ricevuta_file_id ? (
                    <a href={fileUrl(m.ricevuta_file_id)} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`libro-soci-receipt-link-${m.id}`}>
                      <FileText className="h-4 w-4" />Apri
                    </a>
                  ) : m.ricevuta_url ? (
                    <a href={m.ricevuta_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`libro-soci-receipt-url-${m.id}`}>
                      <FileText className="h-4 w-4" />Apri
                    </a>
                  ) : "—"}
                </TableCell>
                <TableCell>
                  <Checkbox checked={m.pagamento_confermato} onCheckedChange={(v) => toggle(m, !!v)} data-testid={`libro-soci-paid-checkbox-${m.id}`} />
                </TableCell>
                  <TableCell>
                    {m.pagamento_confermato ? (
                      <Button size="sm" variant="ghost" disabled={sending === m.id} onClick={() => resend(m)} title="Reinvia email di benvenuto con tessera" data-testid={`libro-soci-resend-email-${m.id}`}>
                        <Mail className={`h-4 w-4 mr-1 ${m.welcome_email_sent ? "text-emerald-600" : "text-amber-600"}`} />{m.welcome_email_sent ? "Reinvia" : "Invia"}
                      </Button>
                    ) : "—"}
                  </TableCell>
                </>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
};
